<template>
  <div class="m-similar-search-results">
    <div class="m-similar-search-results__toggle">
      <GuiToggle
        :field-name="'resultsSwitch'"
        :initial-status="showResults"
        :aria-on="'on'"
        :aria-off="'off'"
        :label="toggleLabel"
        @update-search="toggleShowResults"
      />
    </div>
    <div v-if="showResults" class="m-similar-search-results__results">
      <Module :title="heading" bkgd="light-grey">
        <div v-if="similarSearchResults">
          <div
            v-if="similarSearchResults.time"
            class="m-similar-search-results__time"
          >
            {{ searchTimeText(similarSearchResults.time) }}
          </div>
          <div class="m-similar-search-results__grid">
            <div
              v-for="(items, key) in similarSearchResults.searches"
              :key="key"
              class="m-similar-search-results__row"
            >
              <div class="m-similar-search-results__key-box">
                <pre class="m-similar-search-results__key-text">{{
                  queryParams(key)
                }}</pre>
                <!--
                <ColorPalette
                  v-if="key == 'color' && elasticObject.colors"
                  :colors="elasticObject.colors"
                /> -->
              </div>
              <div class="m-similar-search-results__row-boxes">
                <div
                  v-for="item in items"
                  :key="item.id"
                  class="m-similar-search-results__box"
                >
                  <GuiLink
                    :to="{
                      name: 'object',
                      state: {
                        id: item.id,
                        slug: item.slug_with_path.split('/')[2]
                      },
                      params: {
                        slug: item.slug_with_path.split('/')[2]
                      }
                    }"
                  >
                    <div
                      :class="{
                        'm-similar-search-results__thumbnail': true,
                        'm-similar-search-results__thumbnail--selected':
                          selectedIds.includes(item.id)
                      }"
                    >
                      <ImageResponsive
                        v-if="item.manifest && item.manifest.thumbUuid"
                        :id="item.manifest.thumbUuid"
                        ref="imageResponsive"
                        size="post_xsmall_variable_height"
                      />
                    </div>
                    <pre class="m-similar-search-results__details">{{
                      resultDetails(key, item)
                    }}</pre>
                    <ColorPalette
                      v-if="key.includes('color') && item.colors"
                      :colors="item.colors"
                    />
                  </GuiLink>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="m-similar-search-results__no-results">
          {{ noResults }}
        </div>
      </Module>
    </div>
  </div>
</template>

<script>
import ColorPalette from "@/components/atoms/colorPalette/index.vue";
import { useLocalization } from "@thegetty-private/getty-ui";
import {
  GuiLink,
  ImageResponsive,
  GuiToggle,
  Module
} from "@thegetty-private/getty-ui";

export default {
  name: "SimilarSearchResults",
  components: {
    ColorPalette,
    GuiLink,
    ImageResponsive,
    Module,
    GuiToggle
  },

  props: {
    /**
     * the "path/uuid" id of the HumanMadeObject
     */
    indexedId: {
      type: String,
      required: true
    },
    /**
     * the HumanMadeObject data from the store
     */
    object: {
      type: Object,
      required: false,
      default: undefined
    },
    /**
     * the similar search results
     */
    similarSearchResults: {
      type: Object,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },

  data() {
    return {
      /**
       * The translated heading for the search results section
       */
      heading: this.translatePart("similar_search_results.heading"),
      /**
       * The translated text to display if no results are found
       */
      noResults: this.translatePart("similar_search_results.no_results"),
      /**
       * Whether or not to show search results
       */
      showResults: false,
      /**
       * The translated label for the toggle
       */
      toggleLabel: this.translatePart("similar_search_results.toggle_label")
    };
  },
  computed: {
    /**
     * The ids of the selected images
     * @returns {array}
     */
    selectedIds() {
      if (this.similarSearchResults) {
        return this.similarSearchResults.similar.map((item) => item.id);
      }
      return [];
    }
  },
  methods: {
    /**
     * Returns the string representing the query params used for the displayed query results
     * @param {string} key - the name of the similar objects query
     *
     * @returns {string}
     */
    queryParams(key) {
      // FIXME: rewire
      const esObject = {};
      let caption = "";
      switch (key) {
        case "matdep":
          caption += `Materials: ${esObject.materials} \nDepartment: ${esObject.department}`;
          break;
        case "similar_description":
          caption += `Title & Description: ${esObject.primary_name}`;
          break;
        case "color":
          caption += `Colors: r:${esObject.colors[0].r} g:${esObject.colors[0].g} b:${esObject.colors[0].b}`;
          break;
        case "materials_color":
          caption += "Materials & Color";
          break;
        case "classification_color":
          caption += "Classification & Color";
          break;
        case "culture_color":
          caption += "Culture & Color";
          break;
        case "classification_culture_time":
          caption += `Classification: ${esObject.classification} \nCulture: ${esObject.culture} \nTime Period: ${esObject.fifty_year_range}`;
          break;
        case "place_classification":
          let placesLabel = "";
          let placeFields = ["place_created", "place_depicted", "place_found"];
          for (const place of placeFields) {
            if (place in esObject) {
              placesLabel += `${place}: ${esObject[place]} \n`;
            }
          }
          caption += placesLabel + `Classification: ${esObject.classification}`;
          break;
        default:
          caption += key;
          break;
      }

      if (this.similarSearchResults && this.similarSearchResults.maxScores) {
        let maxScores = this.similarSearchResults.maxScores;
        if (maxScores[key]) {
          caption += `\nMax Score: ${maxScores[key]}`;
        }
      }

      return caption;
    },
    /**
     * Returns the values of the object's fields that matched the similar object query
     * @param {string} key - the name of the similar objects query
     * @param {object} item - the matching object's elastic object's results
     *
     * @returns {string}
     */
    resultDetails(key, item) {
      let caption = "";
      switch (key) {
        case "matdep":
          break;
        case "place_classification":
          let placesLabel = "";
          let placeFields = ["place_created", "place_depicted", "place_found"];
          for (const place of placeFields) {
            if (place in item) {
              placesLabel += `${place}: ${item[place]} \n`;
            }
          }
          caption += placesLabel;
          break;
      }

      if (item.score) {
        caption += `\nScore: ${item.score}`;
      }

      return caption;
    },
    /**
     * The translated text to display the time elapsed for the search
     * @param {string} time - the amount of time the search took
     *
     * @returns {string}
     */
    searchTimeText(time) {
      return this.translatePart("similar_search_results.time", {
        time: time
      });
    },
    /**
     * Event triggered by toggle; switches showResults on and off
     */
    toggleShowResults() {
      this.showResults = !this.showResults;
    }
  }
};
</script>
<style lang="scss">
@import "./index.scss";
</style>
